/**
 * Frontend route paths (React Router). Only routes that actually exist
 * today are defined here — storefront/admin routes are added as each
 * milestone implements them, not hardcoded ahead of time in page code.
 * `signIn`/`signUp` are mounted with a `/*` suffix in the router (Clerk's
 * prebuilt components manage their own sub-routes, e.g. factor selection).
 */
export const ROUTE_NAMES = {
  home: '/',
  signIn: '/sign-in',
  signUp: '/sign-up',
  // Legacy Phase-0 routes -- kept as real redirect *sources* to
  // `accountProfile`/`accountOrders` (see App.tsx) rather than deleted, so
  // no old link/bookmark to `/profile` or `/orders` ever 404s. New code
  // should link to `accountProfile`/`accountOrders` instead.
  profile: '/profile',
  orders: '/orders',
  // Customer Storefront milestone -- category/product detail paths are
  // built as `${category}/${slug}` / `${product}/${slug}` at the call
  // site (dynamic, so not enumerable here), same convention as every
  // admin id-scoped route above.
  shop: '/shop',
  category: '/category',
  product: '/product',
  search: '/search',
  // Marketing pages -- static, public, indexable (see docs/storefront-seo.md).
  about: '/about',
  contact: '/contact',
  cart: '/cart',
  checkout: '/checkout',
  // Order-success path is built as `${orderSuccess}/${orderNumber}` at
  // the call site.
  orderSuccess: '/order-success',
  trackOrder: '/track-order',
  // Account area -- `account` itself redirects to `accountProfile` (same
  // "no page of its own" pattern as `admin` -> `adminDashboard`).
  // Order-detail path is built as `${accountOrders}/${orderNumber}` at
  // the call site.
  account: '/account',
  accountProfile: '/account/profile',
  accountOrders: '/account/orders',
  accountAddresses: '/account/addresses',
  accountWishlist: '/account/wishlist',
  accountRewards: '/account/rewards',
  accountNotifications: '/account/notifications',
  // Public marketing/acquisition page promoting the rewards/spin program --
  // distinct from `accountRewards` (which is the authenticated in-account
  // spin/coupons/history page). Indexable, unlike `accountRewards`.
  earnWithFun: '/earn-with-fun',
  // `/admin` itself has no page of its own -- it redirects to
  // `/admin/dashboard` (see App.tsx). Every future admin section
  // (products, orders, customers, ...) mounts under this same `/admin/*`
  // tree as each milestone builds it; there is no separate
  // `/admin-dashboard` route system.
  admin: '/admin',
  adminDashboard: '/admin/dashboard',
  // Admin Catalog & Inventory milestone -- `adminProducts`/`adminCategories`/
  // etc. are list-page bases; a product/variant detail path is built as
  // `${adminProducts}/${id}` at the call site (dynamic, so not enumerable
  // here), same convention as every other id-scoped route in this app.
  adminProducts: '/admin/products',
  adminProductNew: '/admin/products/new',
  adminCategories: '/admin/categories',
  adminBrands: '/admin/brands',
  adminInventory: '/admin/inventory',
  adminInventoryMovements: '/admin/inventory/movements',
  adminWarehouses: '/admin/warehouses',
  adminMedia: '/admin/media',
  // Admin Dashboard Milestone 3 -- order/customer detail paths are built
  // as `${adminOrders}/${orderNumber}` / `${adminCustomers}/${customerId}`
  // at the call site, same convention as every other id-scoped route here.
  adminOrders: '/admin/orders',
  adminCustomers: '/admin/customers',
  adminPayments: '/admin/payments',
  adminRefunds: '/admin/refunds',
  // Admin Dashboard Milestone 4 -- Marketing (coupons/rewards/banners),
  // Notification operations, Settings, Audit Logs, Reports, System
  // operations. Detail/nested paths (e.g. a single coupon, a role's user
  // list) are built at the call site off these bases, same convention as
  // every other id-scoped route above.
  adminCoupons: '/admin/marketing/coupons',
  adminCouponNew: '/admin/marketing/coupons/new',
  adminRewards: '/admin/marketing/rewards',
  adminBanners: '/admin/marketing/banners',
  // Homepage Banner Management -- a distinct "Content" nav section from
  // "Marketing", scoped specifically to the storefront hero's `home_hero`
  // placement (see `homepage-banners-list-page.tsx`), separate from the
  // general cross-placement banner/campaign manager above.
  adminHomepageBanners: '/admin/content/banners',
  adminNotifications: '/admin/notifications',
  adminSettings: '/admin/settings',
  adminAuditLogs: '/admin/audit-logs',
  adminReports: '/admin/reports',
  adminSystem: '/admin/system',
  forbidden: '/forbidden',
  notFound: '*',
} as const;

export type RouteNameKey = keyof typeof ROUTE_NAMES;
